import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminApi, useAuth } from "@/context/AuthContext";
import PageHead from "@/components/PageHead";
import { renderPostBody } from "@/lib/markdown";
import RichTextEditor from "@/components/RichTextEditor";
import { sanitizeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, EyeOff } from "lucide-react";

function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 200);
}

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  keywords: [],
  cover_image: "",
  published: false,
};

export default function AdminJournalEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [keywordsInput, setKeywordsInput] = useState("");
  const [loaded, setLoaded] = useState(!isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let mounted = true;
    adminApi
      .get(`/journal/admin/id/${id}`)
      .then((r) => {
        if (!mounted) return;
        setForm({
          title: r.data.title || "",
          slug: r.data.slug || "",
          excerpt: r.data.excerpt || "",
          body: r.data.body || "",
          keywords: r.data.keywords || [],
          cover_image: r.data.cover_image || "",
          published: !!r.data.published,
        });
        setKeywordsInput((r.data.keywords || []).join(", "));
        setSlugTouched(true);
        setLoaded(true);
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          logout();
          navigate("/admin/login", { replace: true });
          return;
        }
        setError("Could not load this post.");
        setLoaded(true);
      });
    return () => {
      mounted = false;
    };
  }, [id, isEdit, logout, navigate]);

  // Auto-derive slug from title until user manually edits slug
  useEffect(() => {
    if (isEdit) return;
    if (slugTouched) return;
    setForm((f) => ({ ...f, slug: slugify(f.title) }));
  }, [form.title, slugTouched, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const previewHtml = useMemo(() => renderPostBody(form.body), [form.body]);

  const onKeywordsChange = (v) => {
    setKeywordsInput(v);
    const list = v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setField("keywords", list);
  };

  const onSubmit = async (e, opts = {}) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      ...form,
      slug: slugify(form.slug || form.title),
      published: opts.publish === true ? true : form.published,
    };
    try {
      let saved;
      if (isEdit) {
        const { data } = await adminApi.put(`/journal/${id}`, payload);
        saved = data;
      } else {
        const { data } = await adminApi.post("/journal", payload);
        saved = data;
      }
      toast.success(
        saved.published
          ? "Post published"
          : isEdit
          ? "Draft saved"
          : "Draft created"
      );
      navigate("/admin/journal", { replace: true });
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      const detail = err?.response?.data?.detail;
      setError(
        Array.isArray(detail)
          ? detail.map((d) => d.msg).join(", ")
          : typeof detail === "string"
          ? detail
          : "Save failed."
      );
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && !loaded) {
    return (
      <section className="mx-auto max-w-[1200px] px-6 md:px-10 py-24" data-testid="admin-editor-loading">
        <p className="font-body text-ink-soft">Loading post…</p>
      </section>
    );
  }

  return (
    <div data-testid="admin-editor-page">
      <PageHead
        title={`${isEdit ? "Edit post" : "New post"} — Blog Admin`}
        description="Edit a blog entry."
      />

      <section className="mx-auto max-w-[1200px] px-6 md:px-10 pt-16 md:pt-20 pb-6">
        <Link
          to="/admin/journal"
          className="eyebrow link-underline-grow inline-flex items-center gap-2"
          data-testid="admin-editor-back"
        >
          <ArrowLeft size={12} aria-hidden />
          Back to blog
        </Link>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-display text-4xl md:text-5xl">
            {isEdit ? "Edit " : "New "}
            <em className="not-italic text-brand-red">entry.</em>
          </h1>
          <button
            type="button"
            onClick={() => setPreview((p) => !p)}
            data-testid="admin-editor-preview-toggle"
            className="inline-flex items-center gap-2 border hairline-strong px-4 py-2 hover:border-ink font-ui text-sm"
          >
            {preview ? <EyeOff size={14} /> : <Eye size={14} />}
            {preview ? "Hide preview" : "Show preview"}
          </button>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-6 md:px-10 border-t hairline pt-8 pb-24">
        {error && (
          <div
            className="border hairline-strong bg-paper-alt p-4 mb-6"
            data-testid="admin-editor-error"
            role="alert"
          >
            <p className="font-body text-brand-red-ink text-sm">{error}</p>
          </div>
        )}

        <form
          onSubmit={onSubmit}
          data-testid="admin-editor-form"
          className={`grid gap-8 ${preview ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}
        >
          <div className="space-y-6">
            <div>
              <label htmlFor="post-title" className="block form-label">Title</label>
              <input
                id="post-title"
                data-testid="editor-title"
                type="text"
                required
                maxLength={200}
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                className="mt-2 w-full bg-transparent border-b hairline-strong focus:border-electric outline-none py-3 text-2xl md:text-3xl font-display"
              />
            </div>

            <div>
              <label htmlFor="post-slug" className="block form-label">
                Slug <span className="text-label font-body normal-case">— /blog/{form.slug || "…"}</span>
              </label>
              <input
                id="post-slug"
                data-testid="editor-slug"
                type="text"
                required
                maxLength={200}
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setField("slug", e.target.value);
                }}
                className="mt-2 w-full bg-transparent border-b hairline-strong focus:border-electric outline-none py-2 font-ui text-sm"
              />
            </div>

            <div>
              <label htmlFor="post-excerpt" className="block form-label">
                Excerpt <span className="text-label font-body normal-case">— shown in the archive & meta description</span>
              </label>
              <textarea
                id="post-excerpt"
                data-testid="editor-excerpt"
                rows={3}
                maxLength={500}
                value={form.excerpt}
                onChange={(e) => setField("excerpt", e.target.value)}
                className="mt-2 w-full bg-transparent border hairline-strong focus:border-electric outline-none p-3 font-body leading-relaxed"
              />
              <p className="mt-1 font-ui text-[11px] text-label">
                {form.excerpt.length} / 500
              </p>
            </div>

            <div>
              <label htmlFor="post-keywords" className="block form-label">
                Keywords <span className="text-label font-body normal-case">— comma-separated</span>
              </label>
              <input
                id="post-keywords"
                data-testid="editor-keywords"
                type="text"
                value={keywordsInput}
                onChange={(e) => onKeywordsChange(e.target.value)}
                placeholder="drupal, wcag 2.2, government"
                className="mt-2 w-full bg-transparent border-b hairline-strong focus:border-electric outline-none py-2 font-body"
              />
            </div>

            <div>
              <label htmlFor="post-cover" className="block form-label">
                Cover image URL <span className="text-label font-body normal-case">— optional</span>
              </label>
              <input
                id="post-cover"
                data-testid="editor-cover"
                type="url"
                value={form.cover_image}
                onChange={(e) => setField("cover_image", e.target.value)}
                placeholder="https://…/cover.jpg"
                className="mt-2 w-full bg-transparent border-b hairline-strong focus:border-electric outline-none py-2 font-body"
              />
              {form.cover_image && (
                <div className="mt-3 aspect-[16/9] max-w-md overflow-hidden bg-paper-alt border hairline">
                  <img
                    src={form.cover_image}
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label htmlFor="post-body" className="block form-label">
                Body <span className="text-label font-body normal-case">— write like a doc. Use the toolbar for formatting.</span>
              </label>
              <div className="mt-2" id="post-body">
                <RichTextEditor
                  value={form.body}
                  onChange={(html) => setField("body", html)}
                  placeholder="Start writing…"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 border-t hairline pt-6">
              <input
                id="post-published"
                data-testid="editor-published"
                type="checkbox"
                checked={form.published}
                onChange={(e) => setField("published", e.target.checked)}
                className="h-4 w-4 accent-brand-red"
              />
              <label htmlFor="post-published" className="font-ui text-sm text-ink">
                Published (visible on the public journal)
              </label>
            </div>

            <div className="flex flex-wrap gap-3 border-t hairline pt-6">
              <button
                type="submit"
                disabled={saving || !form.title.trim()}
                data-testid="editor-save"
                className="inline-flex items-center gap-2 bg-ink text-paper px-6 py-3 hover:bg-electric transition-colors disabled:opacity-60 font-ui"
              >
                <Save size={14} />
                {saving ? "Saving…" : "Save"}
              </button>
              {!form.published && (
                <button
                  type="button"
                  onClick={(e) => onSubmit(e, { publish: true })}
                  disabled={saving || !form.title.trim() || !form.body.trim()}
                  data-testid="editor-save-and-publish"
                  className="inline-flex items-center gap-2 bg-brand-red text-paper px-6 py-3 hover:bg-electric transition-colors disabled:opacity-60 font-ui"
                >
                  Save &amp; Publish
                </button>
              )}
              <Link
                to="/admin/journal"
                className="inline-flex items-center gap-2 border hairline-strong px-6 py-3 hover:border-ink font-ui"
              >
                Cancel
              </Link>
            </div>
          </div>

          {preview && (
            <aside
              className="border-t lg:border-t-0 lg:border-l hairline-strong lg:pl-8 pt-8 lg:pt-0"
              data-testid="editor-preview"
            >
              <p className="eyebrow">§ Live preview</p>
              <h2 className="font-display text-3xl md:text-4xl mt-3">
                {form.title || "Untitled entry"}
              </h2>
              {form.excerpt && (
                <p className="mt-3 font-body text-ink-soft leading-relaxed">
                  {form.excerpt}
                </p>
              )}
              <div
                className="prose-redkey mt-6"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewHtml) }}
              />
            </aside>
          )}
        </form>
      </section>
    </div>
  );
}
